import React from 'react';
import classNames from 'classnames/bind';
import * as RadixDialog from '@radix-ui/react-dialog';
import { DateTime } from 'luxon';
import { Skeleton } from '@mantine/core';
import { filesize } from 'filesize';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { LumeoFile } from 'types/file';
import { Tag } from 'types/tag';

import { downloadFile } from 'services/download_file';
import { useAddEntityTags } from 'tags/hooks/useAddEntityTag';
import { useCamera } from 'cameras/hooks/useCamera';
import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';
import { useDeleteEntityTags } from 'tags/hooks/useDeleteEntityTags';
import { useDeleteFiles } from 'files/hooks/useDeleteFiles';
import { useDeployment } from 'deployments/hooks/useDeployment';
import { useFileWithSearchMetadata } from 'files/hooks/useFileWithSearchMetadata';
import { useModal } from 'hooks/useModal';
import { usePipeline } from 'pipelines/hooks/usePipeline';
import { useSpringState } from 'hooks/useSpringState';
import { useStream } from 'streams/hooks/useStream';
import { useUpdateFile } from 'files/hooks/useUpdateFile';
import { useTrackableNames } from 'trackables/hooks';

import { Button } from 'components/Button/Button';
import { ButtonGroup } from 'components/ButtonGroup/ButtonGroup';
import { EntityListItem } from 'components/EntityList';
import { Field } from 'components/Field/Field';
import { FileSearchMetadata } from 'files/components/FileSearchMetadata';
import { Heading } from 'components/Heading/Heading';
import { IconButton } from 'components/IconButton/IconButtonDeprecated';
import { InlineNotification } from 'components/InlineNotification/InlineNotification';
import { LightSwitch } from 'components/LightSwitch/LightSwitch';
import { Separator } from 'components/Separator/Separator';
import { TagsList } from 'tags/components/TagsList/TagsList';
import { Text } from 'components/Text/Text';
import { Textarea } from 'components/Textarea/Textarea';

import styles from './Lightbox.module.scss';

const c = classNames.bind(styles);

type FileFieldValues = { description: string; is_retained: boolean };

export function Controls({ id }: { id: LumeoFile['id'] }) {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const { open, close } = useModal();

  const { data: currentPlan } = useCurrentPlan();

  const { data: file, isLoading } = useFileWithSearchMetadata(id, {
    params: { include_search_metadata: currentPlan?.can_search },
  });
  const trackableIds = React.useMemo(
    () => (Array.isArray(file?.trackables) ? file.trackables : []),
    [file?.trackables]
  );
  const trackableNamesQuery = useTrackableNames(trackableIds);

  const {
    formState: { dirtyFields },
    handleSubmit,
    register,
    reset,
  } = useForm<FileFieldValues>();

  const isDirty = Boolean(Object.keys(dirtyFields).length);

  const [isSuccess, setIsSuccess] = useSpringState(false, 2000);

  const { data: stream, isInitialLoading: isLoadingStream } = useStream(
    file?.stream_id
  );
  const { data: camera, isInitialLoading: isLoadingCamera } = useCamera(
    stream?.camera_id
  );
  const { data: pipeline, isInitialLoading: isLoadingPipeline } = usePipeline(
    file?.pipeline_id
  );
  const { data: deployment, isInitialLoading: isLoadingDeployment } =
    useDeployment(file?.deployment_id);

  const { mutate: mutateFile, isLoading: isUpdating } = useUpdateFile({
    onSuccess({ description, is_retained }) {
      setIsSuccess(true);
      reset({ description, is_retained });
    },
  });

  const { mutateAsync: deleteFiles, isLoading: isDeleting } = useDeleteFiles({
    onSuccess() {
      close('file');
      queryClient.invalidateQueries(['files-search']);
    },
  });

  const { mutate: addTags } = useAddEntityTags({
    entity: 'files',
    listQueryKey: ['files'],
    singleQueryKey: ['file'],
  });

  const { mutate: removeTags } = useDeleteEntityTags({
    entity: 'files',
    listQueryKey: ['files'],
    singleQueryKey: ['file'],
  });

  function onSubmit({ description, is_retained }: FileFieldValues) {
    if (!file) {
      return;
    }

    mutateFile({
      id: file.id,
      description: dirtyFields.description ? description : undefined,
      is_retained: dirtyFields.is_retained ? is_retained : undefined,
    });
  }

  function handleDeleteFile() {
    open('confirm-delete', {
      title: 'Are you sure you want to delete this file?',
      onConfirm: () => deleteFiles({ file_ids: [id] }),
    });
  }

  function handleAddTags(tag: Tag) {
    if (!file) {
      return;
    }

    addTags({ id: file.id, tagIds: [tag.id] });
  }

  function handleRemoveTags(tagId: Tag['id']) {
    if (!file) {
      return;
    }

    removeTags({ id: file.id, tagIds: [tagId] });
  }

  function handleDownload() {
    if (!file || !file.data_url) {
      return;
    }

    downloadFile(file.name, file.data_url);
  }

  function displayDate(file: LumeoFile) {
    const dashboardTimezone = searchParams.get('timezone');

    if (!dashboardTimezone) {
      return DateTime.fromISO(file.created_at).toLocaleString(
        DateTime.DATETIME_MED
      );
    }

    const createdAtWithZone = DateTime.fromISO(file.created_at).setZone(
      dashboardTimezone
    );

    return `${createdAtWithZone.toLocaleString(DateTime.DATETIME_MED)}, ${createdAtWithZone.toFormat('ZZZZ')}`;
  }

  return (
    <div className={c('controls')}>
      <RadixDialog.Close asChild>
        <IconButton
          className={c('close')}
          icon="cancel"
          label="Close"
          onClick={() => close()}
          variant="ghost"
        />
      </RadixDialog.Close>

      <div className={c('info')}>
        {file ? (
          <div>
            <Text size="small" inline>
              {displayDate(file)} / {filesize(file.size)}
            </Text>
            <Heading className={c('heading')} level="2">
              {file.name}
            </Heading>
          </div>
        ) : (
          <div>
            <Skeleton width="50%" height="var(--size-md)" mb="xs" />
            <Skeleton width="80%" height="var(--size-lg)" />
          </div>
        )}

        <div>
          <EntityListItem
            icon="stream"
            type="streams"
            label="Stream"
            loading={isLoadingStream}
            entity={stream}
            onLinkClick={() => close()}
          />
          <EntityListItem
            icon="camera"
            type="cameras"
            label="Camera"
            loading={isLoadingCamera}
            entity={camera}
            onLinkClick={() => close()}
          />
          <EntityListItem
            icon="pipeline"
            type="pipelines"
            label="Workflow"
            loading={isLoadingPipeline}
            entity={pipeline}
            onLinkClick={() => close()}
          />
          <EntityListItem
            icon="deployment"
            type="deployments"
            label="Deployment"
            loading={isLoadingDeployment}
            entity={deployment}
            onLinkClick={() => close()}
          />
        </div>

        {file &&
        (file.objects?.length ||
          file.events?.length ||
          file.texts?.length ||
          file.trackables?.length) ? (
          <FileSearchMetadata
            className={c('search-metadata')}
            layout="detail"
            objects={file.objects}
            events={file.events}
            texts={file.texts}
            trackables={trackableIds}
            trackableItemsLookup={trackableNamesQuery.data?.items}
            trackableListsLookup={trackableNamesQuery.data?.lists}
          />
        ) : null}

        {file && (
          <TagsList
            className={c('tags')}
            defaultValue={file.tags}
            onAdd={handleAddTags}
            onRemove={handleRemoveTags}
          />
        )}
      </div>

      <ButtonGroup>
        <IconButton
          icon="download"
          variant="secondary"
          label="Download"
          disabled={isLoading}
          onClick={handleDownload}
          size="small"
          showLabel
        />
        <IconButton
          icon="delete"
          variant="secondary"
          label="Delete"
          disabled={isLoading}
          loading={isDeleting}
          onClick={handleDeleteFile}
          size="small"
          showLabel
        />
      </ButtonGroup>

      <Separator />

      <form className={c('info')} onSubmit={handleSubmit(onSubmit)}>
        <Field label="Description">
          {file ? (
            <Textarea
              {...register('description')}
              defaultValue={file.description}
            />
          ) : (
            <Skeleton width="100%" height="6.25rem" />
          )}
        </Field>

        {currentPlan?.can_retain_recordings && (
          <Field
            label="Retain"
            // FIXME Mention retention days
            info="Enable to prevent this recording from being deleted after retention period."
          >
            <div className={c('retain')}>
              {file ? (
                <LightSwitch
                  {...register('is_retained')}
                  defaultChecked={file.is_retained}
                />
              ) : (
                <Skeleton width="2.5rem" height="1.5rem" radius="6rem" />
              )}
            </div>
          </Field>
        )}

        <ButtonGroup>
          <Button
            variant="primary"
            type="submit"
            loading={isUpdating}
            disabled={!isDirty || !file}
          >
            Save settings
          </Button>

          {isDirty && <InlineNotification>Unsaved changes</InlineNotification>}

          {isSuccess && (
            <InlineNotification intent="success">Saved!</InlineNotification>
          )}
        </ButtonGroup>
      </form>
    </div>
  );
}
